import { PDFDocument, rgb, StandardFonts, PDFPage, PDFFont } from "pdf-lib";

export interface EmploymentAgreementData {
  companyName: string;
  companyOrgNo: string;
  companyAddress: string;
  workerName: string;
  workerSsn: string;
  workerAddress: string;
  workerEmail: string;
  workerPhone: string;
  roleTitle: string;
  startDate: string;
  endDate?: string;
  employmentType: string;
  probationDuration?: string;
  probationUnit?: string;
  probationEndDate?: string;
  probationBecomes?: string;
  workPercentage: string;
  weeklyHours?: string;
  workScheduleArrangement?: string;
  salary: string;
  salaryType: string;
  fullTimeSalary?: string;
  partTimeSalary?: string;
  vacationDays: string;
  noticeDays: string;
  workPlace?: string;
  collectiveAgreement?: string | null;
  otherTerms?: string;
}

const formatNoticeDays = (days: string | undefined): string => {
  if (!days || !days.trim()) return "-";
  const trimmed = days.trim();
  if (/^\d+$/.test(trimmed)) {
    return `${trimmed} dagar`;
  }
  return trimmed;
};

const formatKr = (val: string | undefined): string => {
  if (!val || !val.trim()) return "-";
  const trimmed = val.trim();
  return trimmed.toLowerCase().includes("kr") ? trimmed : `${trimmed} kr`;
};

export const generateEmploymentAgreementPdf = async (
  data: EmploymentAgreementData,
): Promise<Uint8Array> => {
  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // Modern Color Palette
  const navyColor = rgb(16 / 255, 34 / 255, 64 / 255);
  const darkGray = rgb(60 / 255, 66 / 255, 74 / 255);
  const mutedGray = rgb(120 / 255, 126 / 255, 135 / 255);
  const lightGrayBg = rgb(248 / 255, 249 / 255, 250 / 255);
  const lightBorder = rgb(222 / 255, 226 / 255, 230 / 255);
  const bankidGreenBg = rgb(240 / 255, 248 / 255, 240 / 255);
  const bankidGreenBorder = rgb(200 / 255, 230 / 255, 200 / 255);

  const drawTextOnPage = (
    page: PDFPage,
    text: string,
    x: number,
    y: number,
    size = 10,
    isBold = false,
    color = darkGray,
  ) => {
    page.drawText(text, {
      x,
      y,
      size,
      font: isBold ? boldFont : font,
      color,
    });
  };

  const drawSectionHeader = (page: PDFPage, title: string, y: number) => {
    drawTextOnPage(page, title, 54, y, 11, true, navyColor);
    page.drawLine({
      start: { x: 54, y: y - 6 },
      end: { x: 541, y: y - 6 },
      thickness: 1,
      color: lightBorder,
    });
  };

  const drawWrappedText = (
    page: PDFPage,
    text: string,
    x: number,
    startY: number,
    width: number,
    size = 9,
    fontToUse: PDFFont = font,
    color = darkGray,
  ): number => {
    const lines = text.split("\n");
    let currentY = startY;

    for (const paragraph of lines) {
      const words = paragraph.split(" ");
      let line = "";

      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + " ";
        const textWidth = fontToUse.widthOfTextAtSize(testLine, size);
        if (textWidth > width && n > 0) {
          page.drawText(line.trim(), {
            x,
            y: currentY,
            size,
            font: fontToUse,
            color,
          });
          line = words[n] + " ";
          currentY -= size + 4;
        } else {
          line = testLine;
        }
      }
      if (line.trim().length > 0) {
        page.drawText(line.trim(), {
          x,
          y: currentY,
          size,
          font: fontToUse,
          color,
        });
        currentY -= size + 5;
      }
    }
    return currentY;
  };

  const isProbation =
    Boolean(data.employmentType) &&
    data.employmentType.toLowerCase().includes("provanställning");

  const isTillsvidare =
    Boolean(data.employmentType) &&
    data.employmentType.toLowerCase().includes("tillsvidare");

  // ==========================================
  // PAGE 1: Core Terms & Information
  // ==========================================
  const page1 = pdfDoc.addPage([595.28, 841.89]);

  // Decorative Brand Bar
  page1.drawRectangle({
    x: 54,
    y: 795,
    width: 487,
    height: 6,
    color: navyColor,
  });

  // Title & Subtitle
  drawTextOnPage(page1, "ANSTÄLLNINGSAVTAL", 54, 765, 18, true, navyColor);
  drawTextOnPage(
    page1,
    "Upprättat digitalt enligt gällande villkor och överenskommelser",
    54,
    750,
    9,
    false,
    mutedGray,
  );

  // 1. PARTER
  drawSectionHeader(page1, "1. PARTER", 720);

  // Employer Card (Left Column Box)
  page1.drawRectangle({
    x: 54,
    y: 595,
    width: 235,
    height: 105,
    color: lightGrayBg,
    borderColor: lightBorder,
    borderWidth: 1,
  });
  drawTextOnPage(page1, "Arbetsgivare", 64, 685, 10, true, navyColor);
  drawTextOnPage(page1, `Företag: ${data.companyName || "-"}`, 64, 668, 9);
  drawTextOnPage(page1, `Org.nr: ${data.companyOrgNo || "-"}`, 64, 652, 9);
  drawTextOnPage(page1, `Adress: ${data.companyAddress || "-"}`, 64, 636, 9);

  // Employee Card (Right Column Box)
  page1.drawRectangle({
    x: 306,
    y: 595,
    width: 235,
    height: 105,
    color: lightGrayBg,
    borderColor: lightBorder,
    borderWidth: 1,
  });
  drawTextOnPage(page1, "Arbetstagare", 316, 685, 10, true, navyColor);
  drawTextOnPage(page1, `Namn: ${data.workerName || "-"}`, 316, 668, 9);
  drawTextOnPage(page1, `Personnr: ${data.workerSsn || "-"}`, 316, 652, 9);
  drawTextOnPage(page1, `Adress: ${data.workerAddress || "-"}`, 316, 636, 9);
  drawTextOnPage(page1, `E-post: ${data.workerEmail || "-"}`, 316, 620, 9);
  drawTextOnPage(page1, `Tel: ${data.workerPhone || "-"}`, 316, 604, 9);

  // 2. ANSTÄLLNING
  const section2HeaderY = 570;
  drawSectionHeader(page1, "2. ANSTÄLLNING", section2HeaderY);

  const startDetailsY = 546;

  // Left column
  drawTextOnPage(page1, "Befattning / Roll:", 54, startDetailsY, 9, true, navyColor);
  drawTextOnPage(page1, data.roleTitle || "-", 170, startDetailsY, 9);

  drawTextOnPage(page1, "Anställningsform:", 54, startDetailsY - 18, 9, true, navyColor);
  drawTextOnPage(page1, data.employmentType || "-", 170, startDetailsY - 18, 9);

  let currentLeftY = startDetailsY - 36;
  if (isProbation) {
    const probationDuration = data.probationDuration || "6";
    const probationUnit = data.probationUnit || "månader";
    drawTextOnPage(page1, "Prövotid:", 54, currentLeftY, 9, true, navyColor);
    drawTextOnPage(page1, `${probationDuration} ${probationUnit}`, 170, currentLeftY, 9);
    currentLeftY -= 18;

    if (data.probationEndDate) {
      drawTextOnPage(page1, "Prövotid t.o.m.:", 54, currentLeftY, 9, true, navyColor);
      drawTextOnPage(page1, data.probationEndDate, 170, currentLeftY, 9);
      currentLeftY -= 18;
    }

    drawTextOnPage(page1, "Övergår till:", 54, currentLeftY, 9, true, navyColor);
    drawTextOnPage(
      page1,
      data.probationBecomes || "Tillsvidareanställning",
      170,
      currentLeftY,
      9,
    );
    currentLeftY -= 18;
  }

  drawTextOnPage(page1, "Tillträdesdag:", 54, currentLeftY, 9, true, navyColor);
  drawTextOnPage(page1, data.startDate || "-", 170, currentLeftY, 9);
  currentLeftY -= 18;

  // Right column
  let currentRightY = startDetailsY;
  drawTextOnPage(page1, "Sysselsättningsgrad:", 306, currentRightY, 9, true, navyColor);
  drawTextOnPage(page1, data.workPercentage || "-", 435, currentRightY, 9);
  currentRightY -= 18;

  drawTextOnPage(page1, "Arbetstidsmått:", 306, currentRightY, 9, true, navyColor);
  drawTextOnPage(page1, data.weeklyHours || "-", 435, currentRightY, 9);
  currentRightY -= 18;

  drawTextOnPage(
    page1,
    "Arbetstidens förläggning:",
    306,
    currentRightY,
    9,
    true,
    navyColor,
  );
  drawTextOnPage(
    page1,
    data.workScheduleArrangement || "Enligt schema",
    435,
    currentRightY,
    9,
  );
  currentRightY -= 18;

  // Only display Slutdatum if it's temporary (NOT probation and NOT tillsvidare)
  if (!isProbation && !isTillsvidare && data.endDate) {
    drawTextOnPage(page1, "Slutdatum:", 306, currentRightY, 9, true, navyColor);
    drawTextOnPage(page1, data.endDate, 435, currentRightY, 9);
    currentRightY -= 18;
  }

  const section2BottomY = Math.min(currentLeftY, currentRightY);

  // Arbetsplats row
  let workPlaceVal = data.workPlace || "-";
  if (data.workPlace) {
    const parts = data.workPlace.split(",");
    if (parts.length > 2) {
      workPlaceVal = parts.slice(0, 2).join(",").trim();
    }
  }
  drawTextOnPage(page1, "Arbetsplats:", 54, section2BottomY, 9, true, navyColor);
  drawTextOnPage(page1, workPlaceVal, 170, section2BottomY, 9);

  // 3. LÖN
  const section3HeaderY = section2BottomY - 26;
  drawSectionHeader(page1, "3. LÖN", section3HeaderY);

  const startSalaryY = section3HeaderY - 24;
  const isHourly =
    data.salaryType === "Hourly" ||
    data.salaryType === "Timlön" ||
    (data.employmentType !== undefined &&
      (data.employmentType.toLowerCase().includes("tim") ||
        data.employmentType.toLowerCase().includes("behov") ||
        data.employmentType.toLowerCase().includes("visstid") ||
        data.employmentType === "ExclusiveTemporaryWorker"));

  const numericPct = parseInt((data.workPercentage || "").replace(/[^0-9]/g, ""), 10);
  const isPartTime = !isHourly && !isNaN(numericPct) && numericPct > 0 && numericPct < 100;

  let salaryBottomY = startSalaryY;

  if (isHourly) {
    drawTextOnPage(page1, "Löneform:", 54, startSalaryY, 9, true, navyColor);
    drawTextOnPage(page1, data.salaryType || "Timlön", 170, startSalaryY, 9);

    drawTextOnPage(page1, "Timlön:", 54, startSalaryY - 18, 9, true, navyColor);
    drawTextOnPage(page1, formatKr(data.salary), 170, startSalaryY - 18, 9);
    salaryBottomY = startSalaryY - 18;
  } else if (isPartTime) {
    const fullTimeStr = formatKr(data.fullTimeSalary || data.salary);
    let partTimeStr = data.partTimeSalary;
    if (!partTimeStr) {
      const numBase = parseInt((data.fullTimeSalary || data.salary || "").replace(/[^0-9]/g, ""), 10);
      if (!isNaN(numBase) && numBase > 0) {
        partTimeStr = `${Math.round((numBase * numericPct) / 100)} kr`;
      } else {
        partTimeStr = "-";
      }
    } else {
      partTimeStr = formatKr(partTimeStr);
    }

    drawTextOnPage(page1, "Löneform:", 54, startSalaryY, 9, true, navyColor);
    drawTextOnPage(page1, data.salaryType || "Månadslön", 170, startSalaryY, 9);

    drawTextOnPage(page1, "Heltidslön:", 54, startSalaryY - 18, 9, true, navyColor);
    drawTextOnPage(page1, fullTimeStr, 170, startSalaryY - 18, 9);

    const partTimeLabel = `Månadslön (vid ${data.workPercentage}):`;
    drawTextOnPage(page1, partTimeLabel, 54, startSalaryY - 36, 9, true, navyColor);
    drawTextOnPage(page1, partTimeStr, 170, startSalaryY - 36, 9);
    salaryBottomY = startSalaryY - 36;
  } else {
    drawTextOnPage(page1, "Löneform:", 54, startSalaryY, 9, true, navyColor);
    drawTextOnPage(page1, data.salaryType || "Månadslön", 170, startSalaryY, 9);

    drawTextOnPage(page1, "Månadslön:", 54, startSalaryY - 18, 9, true, navyColor);
    drawTextOnPage(page1, formatKr(data.salary), 170, startSalaryY - 18, 9);
    salaryBottomY = startSalaryY - 18;
  }

  // Right Column Löneutbetalning
  drawTextOnPage(page1, "Löneutbetalning:", 306, startSalaryY, 9, true, navyColor);
  drawTextOnPage(page1, "Månadsvis i efterskott", 435, startSalaryY, 9);

  // 4. VILLKOR
  const section4HeaderY = salaryBottomY - 26;
  drawSectionHeader(page1, "4. VILLKOR", section4HeaderY);

  const startTermsY = section4HeaderY - 24;
  const semesterStr = isHourly
    ? "Semesterersättning utbetalas enligt semesterlagen."
    : `${data.vacationDays || "-"} dagar/år`;

  const collectiveAgreementStr = data.collectiveAgreement
    ? `Ja (${data.collectiveAgreement})`
    : "Nej";

  drawTextOnPage(page1, "Semester:", 54, startTermsY, 9, true, navyColor);
  drawTextOnPage(page1, semesterStr, 170, startTermsY, 9);

  drawTextOnPage(page1, "Uppsägningstid:", 54, startTermsY - 18, 9, true, navyColor);
  drawTextOnPage(page1, formatNoticeDays(data.noticeDays), 170, startTermsY - 18, 9);

  drawTextOnPage(page1, "Kollektivavtal:", 54, startTermsY - 36, 9, true, navyColor);
  drawTextOnPage(page1, collectiveAgreementStr, 170, startTermsY - 36, 9);

  // 5. SÄRSKILDA VILLKOR (Alltid synlig!)
  const section5HeaderY = startTermsY - 58;
  drawSectionHeader(page1, "5. SÄRSKILDA VILLKOR", section5HeaderY);

  if (data.otherTerms && data.otherTerms.trim().length > 0) {
    drawWrappedText(page1, data.otherTerms, 54, section5HeaderY - 20, 487, 8.5);
  } else {
    drawTextOnPage(
      page1,
      "Inga särskilda villkor angivna.",
      54,
      section5HeaderY - 20,
      9,
      false,
      mutedGray,
    );
  }

  // Footer Page 1
  drawTextOnPage(page1, "Sida 1 (2)", 495, 30, 8, false, mutedGray);

  // ==========================================
  // PAGE 2: General Terms & Signing Information
  // ==========================================
  const page2 = pdfDoc.addPage([595.28, 841.89]);

  // Decorative Brand Bar
  page2.drawRectangle({
    x: 54,
    y: 795,
    width: 487,
    height: 6,
    color: navyColor,
  });

  // Title & Subtitle Page 2
  drawTextOnPage(page2, "ANSTÄLLNINGSAVTAL", 54, 765, 18, true, navyColor);
  drawTextOnPage(
    page2,
    "Villkorsbilaga – Övrig information och allmänna villkor",
    54,
    750,
    9,
    false,
    mutedGray,
  );

  // 6. ÖVRIG INFORMATION OM ANSTÄLLNINGEN
  drawSectionHeader(page2, "6. ÖVRIG INFORMATION OM ANSTÄLLNINGEN", 720);

  const hasCollectiveAgreement = Boolean(
    data.collectiveAgreement && data.collectiveAgreement.trim(),
  );

  const statutoryClauses = [
    {
      title: "Övertid och mertid",
      body: hasCollectiveAgreement
        ? "Villkor för övertid och mertid samt eventuell ersättning följer tillämplig lag och kollektivavtal."
        : "Villkor för övertid och mertid samt eventuell ersättning följer tillämplig lag och eventuella särskilda villkor angivna i sektion 5.",
    },
    {
      title: "Arbetstidens förläggning",
      body: hasCollectiveAgreement
        ? "Arbetstidens förläggning och framförhållning vid förändringar följer tillämplig lag och kollektivavtal."
        : "Arbetstidens förläggning och framförhållning vid förändringar följer tillämplig lag och eventuella särskilda villkor angivna i sektion 5.",
    },
    {
      title: "Sjuklön och sociala avgifter",
      body: hasCollectiveAgreement
        ? "Arbetsgivaren betalar sociala avgifter enligt gällande regler. Rätt till sjuklön gäller enligt lag och kollektivavtal."
        : "Arbetsgivaren betalar sociala avgifter enligt gällande regler. Rätt till sjuklön gäller enligt lag och eventuella särskilda villkor angivna i sektion 5.",
    },
    {
      title: "Tjänstepension och försäkringar",
      body: hasCollectiveAgreement
        ? "Tjänstepension och försäkringar gäller enligt arbetsgivarens villkor och kollektivavtal."
        : "Tjänstepension och försäkringar gäller enligt arbetsgivarens villkor och eventuella särskilda villkor angivna i sektion 5.",
    },
    {
      title: "Utbildning",
      body: hasCollectiveAgreement
        ? "Rätt till utbildning som tillhandahålls av arbetsgivaren gäller enligt arbetsgivarens villkor och kollektivavtal."
        : "Rätt till utbildning som tillhandahålls av arbetsgivaren gäller enligt arbetsgivarens villkor och eventuella särskilda villkor angivna i sektion 5.",
    },
    {
      title: "Anställningens upphörande",
      body: hasCollectiveAgreement
        ? "Villkor och förfarande vid anställningens upphörande följer anställningsformen, tillämplig lag och kollektivavtal."
        : "Villkor och förfarande vid anställningens upphörande följer anställningsformen, tillämplig lag och eventuella särskilda villkor angivna i sektion 5.",
    },
  ];

  let clauseY = 696;
  for (const clause of statutoryClauses) {
    drawTextOnPage(page2, clause.title, 54, clauseY, 9.5, true, navyColor);
    clauseY -= 13;
    clauseY = drawWrappedText(page2, clause.body, 54, clauseY, 487, 8.5, font, darkGray);
    clauseY -= 10;
  }

  // 7. ELEKTRONISK SIGNERING
  const bannerY = Math.min(clauseY - 15, 230);
  page2.drawRectangle({
    x: 54,
    y: bannerY - 55,
    width: 487,
    height: 65,
    color: bankidGreenBg,
    borderColor: bankidGreenBorder,
    borderWidth: 1,
  });

  drawTextOnPage(
    page2,
    "7. ELEKTRONISK SIGNERING",
    64,
    bannerY - 5,
    10,
    true,
    rgb(46 / 255, 117 / 255, 46 / 255),
  );
  drawTextOnPage(
    page2,
    "Detta avtal har upprättats digitalt. Parterna signerar avtalet elektroniskt via svenskt BankID.",
    64,
    bannerY - 22,
    9,
  );
  drawTextOnPage(
    page2,
    "Fullständiga elektroniska verifikationssignaturer bifogas detta dokument efter slutförande.",
    64,
    bannerY - 37,
    9,
  );

  // Footer Page 2
  drawTextOnPage(page2, "Sida 2 (2)", 495, 30, 8, false, mutedGray);

  return await pdfDoc.save();
};
